import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A card's own markup, drawn: its template as a tree of the SDK's own elements, inside a shadow
 * root that carries the card's style — so the style reaches nothing outside the card, and the
 * page's rules do not reach in. The theme's variables still do (custom properties inherit through a
 * shadow root), so `var(--tone-red)` follows light and dark like everything else.
 *
 * Nothing here is ever written as HTML: the template was read into a tree by `cleanCardTemplate`
 * (`@erp-sslab/schema/card-markup`, the reader identity and the assistant use), and each value is a
 * text child React escapes. The tree is read again here before drawing, whatever was stored.
 */
import { createElement, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cleanCardStyle, cleanCardTemplate } from '@erp-sslab/schema/card-markup';
import { readPath } from "./home-model.js";
/**
 * What every card's shadow root starts from: nothing inherited from the page's rules (`all:
 * initial`), the page's font and ink, a table that reads as one, and the look of the one control
 * the SDK may place inside — a form's button. The card's own style comes after it.
 */
export const MARKUP_BASE = [
    ':host{all:initial;display:block;min-width:0;min-height:0;overflow:hidden;contain:paint;color:var(--foreground);font-family:inherit;font-size:13px;line-height:1.5}',
    '*,*::before,*::after{box-sizing:border-box}',
    'h3,h4,h5,p,ul,ol{margin:0}',
    'h3{font-size:15px}h4{font-size:14px}h5{font-size:13px}',
    'table{border-collapse:collapse;width:100%}',
    'th,td{text-align:start;padding:3px 6px;vertical-align:top}',
    'th{font-weight:600;color:var(--muted-foreground)}',
    'hr{border:0;border-top:1px solid var(--border);margin:6px 0}',
    'button[data-card-action]{font:inherit;font-size:12px;line-height:1.4;border:1px solid var(--border);background:var(--card);color:var(--foreground);border-radius:6px;padding:1px 8px;cursor:pointer}',
    'button[data-card-action]:hover{background:var(--muted)}',
    'button[data-card-action]:focus-visible{outline:2px solid var(--ring);outline-offset:1px}',
].join('\n');
/** The host's shadow root, made once: open, so the page's own tests can look in; the page's rules never do. */
export function shadowOf(host) {
    return host.shadowRoot ?? host.attachShadow({ mode: 'open' });
}
/**
 * A box whose children are drawn in its own shadow root, under `css`. Server-side (no DOM) it draws
 * the empty box; the browser fills it once mounted.
 */
export function ShadowMarkup({ css, dir, children, ...rest }) {
    const host = useRef(null);
    const [root, setRoot] = useState(null);
    useLayoutEffect(() => { if (host.current)
        setRoot(shadowOf(host.current)); }, []);
    return (_jsx("div", { ref: host, "data-home-markup": "", ...rest, children: root ? createPortal(_jsxs(_Fragment, { children: [_jsx("style", { children: `${MARKUP_BASE}\n${css}` }), _jsx("div", { dir: dir, "data-card-markup-root": "", children: children })] }), root) : null }));
}
/** A card's style and template, read again before drawing: what may be drawn, and what may not. */
export function readMarkup(card) {
    const style = card.style ? cleanCardStyle(card.style) : { css: '', stripped: [] };
    const template = card.template ? cleanCardTemplate(card.template, card.shape, (card.forms ?? []).map((f) => f.action)) : { nodes: [], fields: [], stripped: [] };
    return { css: style.css, nodes: template.nodes, stripped: [...style.stripped, ...template.stripped] };
}
/**
 * The template's tree as React elements. Every value is a string child — React writes it as text,
 * so a value holding `<script>` is drawn as those characters. Only the tags the reader kept are
 * created, with only `className`, `dir`, `colSpan` and `rowSpan`.
 */
export function renderTemplate(nodes, scope, row, item, parent = '') {
    return nodes.map((n, i) => {
        switch (n.k) {
            // Blank text between a table's rows or a list's items is layout in the template, not content — and not valid there.
            case 'text': return BLANK_INSIDE.has(parent) && !n.v.trim() ? null : n.v;
            case 'field': {
                const value = row ? readPath(row, n.path) : item ? item[n.path] : scope.context[n.path];
                return scope.format(value);
            }
            case 'action': return _jsx("span", { "data-card-action-slot": n.target, children: scope.action(n.target, row) }, i);
            case 'each': {
                if (n.over === 'rows') {
                    return (scope.context.rows ?? []).map((r, j) => {
                        const id = readPath(r, 'id');
                        return _jsx(FragmentKeyed, { children: renderTemplate(n.kids, scope, r, undefined, parent) }, typeof id === 'string' || typeof id === 'number' ? `r${id}` : `i${j}`);
                    });
                }
                return (scope.context.items ?? []).map((it) => _jsx(FragmentKeyed, { children: renderTemplate(n.kids, scope, undefined, it, parent) }, it.key));
            }
            case 'el': {
                const props = { key: i };
                if (n.attrs.class)
                    props['className'] = n.attrs.class;
                if (n.attrs.dir)
                    props['dir'] = n.attrs.dir;
                if (n.attrs.colspan)
                    props['colSpan'] = n.attrs.colspan;
                if (n.attrs.rowspan)
                    props['rowSpan'] = n.attrs.rowspan;
                const kids = renderTemplate(n.kids, scope, row, item, n.tag);
                return createElement(n.tag, props, ...(kids.length ? kids : []));
            }
        }
    });
}
const BLANK_INSIDE = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'ul', 'ol']);
function FragmentKeyed({ children }) {
    return _jsx(_Fragment, { children: children });
}
/** The reader itself, for the shell: one reading of a card's markup everywhere. */
export { CARD_MARKUP_LIMITS, cleanCardMarkup, cleanCardScript, cleanCardStyle, cleanCardTemplate, formTargetProblem, } from '@erp-sslab/schema/card-markup';
//# sourceMappingURL=home-markup.js.map